import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BookCard from '../components/BookCard';
import { apiUrl } from '../api';

// Gutenberg cover images used as decorative floating elements
const FLOATING_COVERS = [
  'https://www.gutenberg.org/cache/epub/1342/pg1342.cover.medium.jpg', // Pride & Prejudice
  'https://www.gutenberg.org/cache/epub/11/pg11.cover.medium.jpg',    // Alice in Wonderland
  'https://www.gutenberg.org/cache/epub/84/pg84.cover.medium.jpg',    // Frankenstein
  'https://www.gutenberg.org/cache/epub/1080/pg1080.cover.medium.jpg',// A Modest Proposal
  'https://www.gutenberg.org/cache/epub/2701/pg2701.cover.medium.jpg',// Moby Dick
];

const STATS = [
  { value: '3M+', label: 'Free Books', icon: '📚' },
  { value: '36+', label: 'Categories', icon: '🗂️' },
  { value: '100%', label: 'Free Forever', icon: '♾️' },
  { value: '∞', label: 'Reading Hours', icon: '⏱️' },
];

const FEATURES = [
  {
    emoji: '📚',
    title: '3M+ Books',
    desc: 'Every public domain book ever written — from ancient Vedas to Victorian classics.',
    gradient: 'from-accent/20 to-purple-900/20',
    border: 'border-accent/20',
  },
  {
    emoji: '🔍',
    title: 'Smart Search',
    desc: 'Find any book instantly by title, author, subject, or language.',
    gradient: 'from-blue-900/20 to-cyan-900/20',
    border: 'border-blue-700/20',
  },
  {
    emoji: '📱',
    title: 'Read Anywhere',
    desc: 'Native inline reader — no app downloads, no sign-ups, no cost.',
    gradient: 'from-emerald-900/20 to-teal-900/20',
    border: 'border-emerald-700/20',
  },
];

const CATEGORIES = [
  {
    name: 'Indian Mythology',
    emoji: '🕉️',
    subject: 'Hindu mythology',
    gradient: 'linear-gradient(135deg, #3b1f6b 0%, #6d28d9 100%)',
    shadow: 'rgba(109,40,217,0.3)',
  },
  {
    name: 'Fiction',
    emoji: '📖',
    subject: 'Fiction',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    shadow: 'rgba(37,99,235,0.3)',
  },
  {
    name: 'Philosophy',
    emoji: '🤔',
    subject: 'Philosophy',
    gradient: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',
    shadow: 'rgba(22,163,74,0.25)',
  },
  {
    name: 'Science',
    emoji: '🔬',
    subject: 'Science',
    gradient: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
    shadow: 'rgba(8,145,178,0.3)',
  },
  {
    name: 'Yoga & Meditation',
    emoji: '🧘',
    subject: 'Yoga',
    gradient: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)',
    shadow: 'rgba(234,88,12,0.3)',
  },
  {
    name: 'History',
    emoji: '🏛️',
    subject: 'History',
    gradient: 'linear-gradient(135deg, #44403c 0%, #92400e 100%)',
    shadow: 'rgba(146,64,14,0.3)',
  },
];

// Skeleton placeholder card
function SkeletonCard() {
  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="skeleton-shimmer" style={{ paddingTop: '140%', position: 'relative' }}>
        <div className="absolute inset-0 bg-primary/50" />
      </div>
      <div className="p-4 space-y-3">
        <div className="h-3 skeleton rounded w-1/3" />
        <div className="h-4 skeleton rounded w-full" />
        <div className="h-4 skeleton rounded w-3/4" />
        <div className="h-3 skeleton rounded w-1/2" />
        <div className="h-8 skeleton rounded-lg w-full mt-2" />
      </div>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchFeatured = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(apiUrl('/api/books/featured'));
        const data = await res.json();
        if (!cancelled) setFeaturedBooks(data.results || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch featured books:', err.message);
          setError('Failed to load featured books. Is the backend running?');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFeatured();
    return () => { cancelled = true; };
  }, []);

  const handleHeroSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="flex flex-col">

      {/* ── HERO SECTION ─────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden flex flex-col items-center justify-center text-center"
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a14 0%, #1a0a2e 50%, #0a1428 100%)',
        }}
      >
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-float"
            style={{ background: 'radial-gradient(circle, #e94560 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-15 animate-float-delay"
            style={{ background: 'radial-gradient(circle, #6d28d9 0%, transparent 70%)' }}
          />
          <div
            className="absolute top-1/3 right-1/3 w-64 h-64 rounded-full blur-3xl opacity-10 animate-float-delay-2"
            style={{ background: 'radial-gradient(circle, #f5a623 0%, transparent 70%)' }}
          />
          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                'linear-gradient(#e94560 1px, transparent 1px), linear-gradient(to right, #e94560 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }}
          />
        </div>

        {/* Floating book covers */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {FLOATING_COVERS.map((src, i) => {
            const positions = [
              { top: '8%', left: '3%' },
              { top: '15%', right: '4%' },
              { bottom: '20%', left: '5%' },
              { bottom: '10%', right: '6%' },
              { top: '50%', left: '1%' },
            ];
            const pos = positions[i] || {};
            const delays = ['', 'animate-float-delay', 'animate-float-delay-2', 'animate-float', 'animate-float-delay'];
            return (
              <div
                key={i}
                className={`absolute hidden xl:block ${delays[i]} opacity-25`}
                style={{ ...pos, transform: `rotate(${(i % 2 === 0 ? 1 : -1) * (5 + i * 2)}deg)` }}
              >
                <img
                  src={src}
                  alt=""
                  className="w-24 rounded-lg shadow-2xl"
                  style={{ filter: 'blur(0.5px)' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            );
          })}
        </div>

        {/* Hero content */}
        <div className="relative z-10 page-container flex flex-col items-center gap-6 py-20 md:py-24">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border border-accent/30 bg-accent/10
                          text-accent text-sm font-medium px-4 py-1.5 rounded-full animate-fade-in-up">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            Free &amp; Open Source · No Account Required
          </div>

          {/* Headline */}
          <h1
            className="font-display font-bold leading-tight max-w-4xl mx-auto animate-fade-in-up"
            style={{
              fontSize: 'clamp(2.5rem, 8vw, 5.5rem)',
              animationDelay: '0.1s',
              animationFillMode: 'both',
            }}
          >
            <span className="text-white">The World's Books,</span>
            <br />
            <span className="gradient-text">All Free.</span>
          </h1>

          {/* Subheading */}
          <p
            className="text-gray-400 text-lg md:text-xl max-w-xl mx-auto leading-relaxed animate-fade-in-up"
            style={{ animationDelay: '0.2s', animationFillMode: 'both' }}
          >
            3 million books. Zero cost. From ancient Vedas to modern philosophy.
          </p>

          {/* Inline search bar */}
          <form
            onSubmit={handleHeroSearch}
            className="w-full max-w-xl flex gap-0 animate-fade-in-up"
            style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
          >
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, author, subject..."
                className="w-full pl-12 pr-4 py-4 rounded-l-xl bg-white/10 border border-white/20
                           text-white placeholder-gray-400 text-sm
                           focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                           backdrop-blur-sm transition-all duration-200"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-4 rounded-r-xl font-semibold text-white text-sm flex-shrink-0
                         transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #e94560, #c73651)' }}
            >
              Search
            </button>
          </form>

          {/* CTA buttons */}
          <div
            className="flex items-center gap-4 flex-wrap justify-center animate-fade-in-up"
            style={{ animationDelay: '0.4s', animationFillMode: 'both' }}
          >
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm
                         text-white transition-all duration-200 hover:shadow-2xl hover:shadow-accent/40
                         hover:-translate-y-0.5 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #e94560, #f5a623)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13
                     C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477
                     14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477
                     18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Start Reading Free
            </Link>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-sm
                         text-white border border-white/20 bg-white/5
                         transition-all duration-200 hover:bg-white/10 hover:border-white/30
                         hover:-translate-y-0.5 active:scale-95 backdrop-blur-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              Browse 36 Categories
            </Link>
          </div>

          {/* Scroll hint */}
          <div className="mt-12 flex flex-col items-center gap-2 text-gray-600 text-xs animate-float">
            <span>Scroll to explore</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ────────────────────────────────────────────────── */}
      <section className="border-y border-border" style={{ background: 'rgba(26,26,46,0.8)' }}>
        <div className="page-container py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center group">
                <div className="text-2xl mb-2">{stat.icon}</div>
                <div
                  className="font-display text-3xl md:text-4xl font-bold mb-1"
                  style={{ color: '#e94560' }}
                >
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── "NEW TO LIBREBOOKS?" FEATURE CARDS ────────────────────────── */}
      <section className="py-20">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">
              New to LibreBooks?
            </h2>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Everything you need for a lifetime of free reading, built right in.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className={`rounded-2xl p-7 border ${f.border} bg-gradient-to-br ${f.gradient}
                             glass-card hover:-translate-y-1 transition-transform duration-300`}
              >
                <div className="text-4xl mb-4">{f.emoji}</div>
                <h3 className="font-display text-xl font-bold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED CLASSICS GRID ────────────────────────────────────── */}
      <section className="py-16" style={{ background: 'rgba(15,15,26,0.8)' }}>
        <div className="page-container">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <h2 className="section-title">Featured Classics</h2>
              <p className="text-gray-400 mt-2 text-sm">
                The most popular public domain books of all time
              </p>
            </div>
            <Link
              to="/browse"
              className="text-accent hover:text-accent-light text-sm font-medium
                         flex items-center gap-1.5 transition-colors duration-200"
            >
              View all categories
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {error && (
            <div className="rounded-xl border border-red-800/50 bg-red-900/10 p-6 text-center">
              <p className="text-red-400 font-medium mb-2">Unable to Load Books</p>
              <p className="text-gray-500 text-sm">{error}</p>
              <p className="text-gray-600 text-xs mt-2">
                Make sure the backend is running:{' '}
                <code className="text-accent">cd backend && npm run dev</code>
              </p>
            </div>
          )}

          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {Array.from({ length: 20 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {!loading && !error && featuredBooks.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {featuredBooks.map((book) => (
                <BookCard key={`${book.source}-${book.id}`} book={book} />
              ))}
            </div>
          )}

          {!loading && !error && featuredBooks.length === 0 && (
            <div className="text-center py-20 text-gray-500">
              <span className="text-5xl block mb-4">📭</span>
              <p>No books found. Please try again later.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── CATEGORY SHOWCASE ────────────────────────────────────────── */}
      <section className="py-20">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title mb-3">Explore by Category</h2>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Dive into a world of knowledge — every genre, every era.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.name}
                to={`/browse?subject=${encodeURIComponent(cat.subject)}`}
                className="group relative rounded-2xl overflow-hidden p-6 flex flex-col gap-3
                           border border-white/10 hover:border-white/20
                           transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                style={{
                  background: cat.gradient,
                  boxShadow: `0 4px 24px ${cat.shadow}`,
                }}
              >
                <span className="text-4xl">{cat.emoji}</span>
                <span className="font-display text-lg font-bold text-white">{cat.name}</span>
                <div className="flex items-center gap-1 text-white/60 text-xs font-medium
                                group-hover:text-white/90 transition-colors duration-200">
                  Browse
                  <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                {/* Hover shimmer */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUOTE SECTION ────────────────────────────────────────────── */}
      <section className="py-20 border-y border-border">
        <div className="page-container">
          <div
            className="max-w-3xl mx-auto rounded-3xl p-10 md:p-14 text-center glass-card"
            style={{ background: 'rgba(255,255,255,0.03)' }}
          >
            {/* Decorative open-quote */}
            <div
              className="font-display text-8xl leading-none mb-4 opacity-20 select-none"
              style={{ color: '#e94560' }}
              aria-hidden="true"
            >
              "
            </div>
            <blockquote>
              <p
                className="font-display text-2xl md:text-3xl font-bold text-white leading-relaxed mb-6"
                style={{ fontStyle: 'italic' }}
              >
                Reading is to the mind what exercise is to the body.
              </p>
              <footer className="text-gray-400 text-sm font-medium tracking-widest uppercase">
                — Joseph Addison
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;
